// UI adapter — maps BuyAgainCard (from Redux/buyAgain) to the render props
// the BuyAgainPage product grid cards expect. The page was originally built
// against a static BuyAgainProduct shape; this adapter preserves that shape
// so the grid JSX needs minimal changes.
import type { BuyAgainCard } from "../../../Redux/buyAgain/types";

export type BuyAgainDealType =
  | "limited"
  | "special"
  | "seasonal"
  | "premium"
  | "exclusive";

export type BuyAgainDeliveryType = "delivery" | "fast" | "stock";

export type BuyAgainProductColor = {
  name: string;
  value: string;
};

export type BuyAgainProduct = {
  id: string;                // MongoDB ObjectId string (was: number)
  badge: string;
  title: string;
  description: string;
  image: string;
  colors: BuyAgainProductColor[];
  extraColors: number;
  rating: number;
  reviews: string;
  deal: string | null;
  dealType: BuyAgainDealType;
  price: number;
  mrp: number;
  delivery: string;
  deliveryType: BuyAgainDeliveryType;
  category: string;
  purchasedDaysAgo: number;
  purchaseCount: number;
  stockStatus: string;
};

/** Format a review count for compact display (1234 → "1.2K"). */
const formatReviewCount = (count: number): string => {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return String(count);
};

/** Derive a delivery label from live catalog flags. */
const deriveDeliveryLabel = (live: BuyAgainCard["live"]): string => {
  if (!live) return "Check availability";
  if (live.stockStatus === "out_of_stock") return "Out of stock";
  if (live.nextDayDelivery) return "Next Day Delivery";
  if (live.fastDelivery) return "Fast Delivery Available";
  if (live.freeDelivery) return "Free Delivery";
  if (live.stockStatus === "low_stock") return "Only few left in stock";
  return "In Stock";
};

/** Derive a delivery type bucket from live catalog flags. */
const deriveDeliveryType = (live: BuyAgainCard["live"]): BuyAgainDeliveryType => {
  if (!live) return "stock";
  if (live.stockStatus === "out_of_stock") return "stock";
  if (live.fastDelivery || live.nextDayDelivery) return "fast";
  if (live.freeDelivery) return "delivery";
  return "stock";
};

/** Derive a deal type bucket from the live deal/coupon labels. */
const deriveDealType = (live: BuyAgainCard["live"]): BuyAgainDealType => {
  if (!live) return "limited";
  if (live.dealLabel) return "limited";
  if (live.couponLabel) return "special";
  return "limited";
};

/**
 * Map a BuyAgainCard (order metadata + live catalog data) to the flat
 * BuyAgainProduct shape the grid cards use. Falls back to order data when
 * live catalog data is unavailable (delisted product or fetch failure).
 */
export function mapBuyAgainCardToProduct(card: BuyAgainCard): BuyAgainProduct {
  const live = card.live;
  const colors = (live?.colors ?? []).map((c) => ({ name: c, value: c }));
  const discountPercent = live
    ? live.discountPercent
    : card.orderPrice > 0
      ? 0
      : 0;

  return {
    id: card.productId,
    badge:
      card.purchaseCount > 1
        ? `BOUGHT ${card.purchaseCount}×`
        : "BOUGHT BEFORE",
    title: card.title,
    description: live?.description ?? "",
    image: live?.mainImage || card.orderImage,
    colors,
    extraColors: colors.length > 5 ? colors.length - 5 : 0,
    rating: live?.rating ?? 0,
    reviews: live ? formatReviewCount(live.reviewCount) : "0",
    deal: live?.dealLabel ?? live?.couponLabel ?? null,
    dealType: deriveDealType(live),
    price: live?.price ?? card.orderPrice,
    mrp: live?.mrp ?? card.orderPrice,
    delivery: deriveDeliveryLabel(live),
    deliveryType: deriveDeliveryType(live),
    category: live?.category || card.orderCategory,
    purchasedDaysAgo: card.purchasedDaysAgo,
    purchaseCount: card.purchaseCount,
    stockStatus: live?.stockStatus ?? "in_stock",
  };
}
